<template>
	<div class="app-input-wrapper" :class="{ 'app-input-wrapper__unstyled': unstyled }">
		<label @click="focusOnInputEl">
			<div v-if="label" class="app-input-label">
				{{ label }}
				<span v-if="requiredField" class="app-input-required">*</span>
			</div>
			<textarea
				ref="inputEl"
				v-model.trim="model"
				:name="name"
				:cols="cols"
				:rows="rows"
				:disabled="disabled"
				:placeholder="placeholder"
				:readonly="readonly"
				:spellcheck="spellcheck"
				:wrap="wrap"
				class="app-input-element"
				:class="{
					'input-valid': !error?.length,
					'input-invalid': error?.length,
					'input-disabled': disabled,
				}"
				@blur="handleBlur"
				@change="emits('change', $event)"
				@input="onInput($event as InputEvent)"
				@focus="handleFocus"
			></textarea>
		</label>

		<div class="app-input-error">
			<span v-if="error">{{ error }}</span>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, useTemplateRef, onMounted } from 'vue';

	interface IProps {
		label?: string;
		placeholder?: string;
		name?: string;
		error?: string;
		disabled?: boolean;
		focusOnMount?: boolean;
		rows?: string;
		cols?: string;
		readonly?: boolean;
		unstyled?: boolean;
		spellcheck?: boolean;
		wrap?: 'soft' | 'hard';
		autoResize?: boolean;
		maxRows?: number;
		requiredField?: boolean;
	}

	const props = withDefaults(defineProps<IProps>(), {
		label: '',
		name: '',
		rows: '1',
		cols: '',
		disabled: false,
		placeholder: '',
		readonly: false,
		spellcheck: false,
		wrap: 'soft',
		error: undefined,
		autoResize: false,
		maxRows: 5,
		requiredField: false,
	});

	const model = defineModel<string>({ required: true, default: '' });
	const emits = defineEmits(['blur', 'change', 'input', 'focus']);

	const isFocused = ref(false);
	const inputEl = useTemplateRef('inputEl');

	onMounted(() => {
		if (props.focusOnMount) {
			focusOnInputEl();
		}
	});

	function handleFocus($event: FocusEvent) {
		emits('focus', $event);
		isFocused.value = true;
	}

	function handleBlur($event: FocusEvent) {
		emits('blur', $event);
		isFocused.value = false;
	}

	function focusOnInputEl() {
		inputEl.value?.focus();
	}

	function onInput($event: InputEvent) {
		emits('input', $event);

		if (props.autoResize && inputEl.value) {
			const maxHeight = props.maxRows * 16 + 24;
			const height = inputEl.value.style.height.replace('px', '');

			if (+height < maxHeight) {
				inputEl.value.style.height = '16px';
				inputEl.value.style.height = inputEl.value.scrollHeight + 'px';
				inputEl.value.style.overflowY = 'hidden';
			} else {
				inputEl.value.style.overflowY = 'auto';
			}
		}
	}
</script>

<style scoped lang="scss">
	.app-input-wrapper {
		.app-input-element {
			height: auto;
			width: 100%;
			padding: 9px 14px;
			overflow-wrap: break-word;

			::-webkit-scrollbar-track {
				background-color: transparent;
			}

			::-webkit-scrollbar-thumb {
				background-color: var(--icon-primary-color);
				border-radius: 5px;
			}
		}
	}

	.app-input-wrapper__unstyled {
		.app-input-element {
			padding: 0;
			border: 0;
			background-color: inherit;

			&:hover,
			&:focus,
			&:focus-within,
			&:focus-visible {
				background-color: inherit;
			}
		}
	}

	.input-el {
		width: 100%;
		height: 100%;
	}
</style>
